import {
  ChangeEventHandler,
  forwardRef,
  Ref,
  useCallback,
  useState
} from 'react';
import { TextFieldProps } from './types';
import clsx from 'clsx';
import Field from '../Field';
import IconButton from '../IconButton';
import IconEyeOpen from '../../icons/IconEyeOpen';
import IconEyeClosed from '../../icons/IconEyeClosed';
import { numeric } from '../../../utils/form';

/**
 *  UI Component
 */
const TextField = forwardRef<HTMLDivElement, TextFieldProps>(function TextField(
  props,
  ref
) {
  const {
    className,
    value,
    defaultValue,
    name,
    min,
    max,
    placeholder,
    type,
    step,
    inputMode,
    onChange,
    onInputChange,
    onFocus,
    showFocus = true,
    onBlur,
    multiline,
    rows,
    inputRef,
    inputProps,
    textareaProps,
    showPasswordToggle,
    showComma,
    endAdornment,
    maxLength,
    pattern,
    disabled,
    readOnly,
    toFixed,
    ...rest
  } = props;

  const [focused, setFocused] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleInputMode = useCallback<
    ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  >(
    (e) => {
      if (inputMode === 'numeric') numeric(e, inputProps, toFixed);
    },
    [inputMode]
  );

  const handleCommaRequirements = (value: string): string | false => {
    const newVal = value.replace(/[,a-zA-Z]/g, '');
    const NumberVal = Number(newVal);
    if (typeof max === 'number' && NumberVal >= max) return false;
    if (typeof min === 'number' && NumberVal < min) return false;
    if (newVal.endsWith('..')) return false;
    if (/[.\-/\\].*[.\-/\\]/.test(newVal)) return false;

    return newVal;
  };

  const handleChange = useCallback<
    ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  >(
    (e) => {
      const data = (e.nativeEvent as InputEvent).data;
      if ((data === 'e' || data === 'E') && type === 'number') return;
      handleInputMode(e);
      if (type === 'number' && showComma) {
        const value = e?.target?.value ?? '';
        const newVal = handleCommaRequirements(value);

        e.target.value = typeof newVal === 'string' ? newVal : '';
      }
      if (onInputChange) {
        onInputChange(e);
      }
      if (onChange) {
        onChange(e.target.value);
      }
    },
    [onChange, onInputChange]
  );

  const handleFocus = useCallback(() => {
    if (onFocus) onFocus();
    if (!showFocus) return null;
    setFocused(true);
  }, [setFocused, onFocus, showFocus]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    if (onBlur) onBlur();
  }, [setFocused, onBlur]);

  const cls = styleTextField(props);

  // Show password toggle logic
  let endAdornmentComp = endAdornment;
  if (showPasswordToggle && type === 'password') {
    endAdornmentComp = (
      <IconButton
        /* to prevent focus and not disrupt tab key navigation, please use this on all adornments that don't require focus */
        tabIndex={-1}
        variant="text"
        rounded="full"
        onClick={() => {
          setShowPass(!showPass);
        }}
      >
        {showPass ? <IconEyeOpen /> : <IconEyeClosed />}
      </IconButton>
    );
  }

  const inputElProps = {
    type: showPass ? 'text' : type,
    name,
    min,
    max,
    placeholder:
      placeholder || (type === 'password' ? '••••••••••••••••' : undefined),
    defaultValue,
    className: clsx(cls.input),
    onChange: handleChange,
    value: value,
    onFocus: handleFocus,
    onBlur: handleBlur
  };

  const useCommaOrValue = showComma
    ? value?.toString()?.endsWith('.') || value?.toString()?.endsWith('.0')
      ? value
      : Number(value).toLocaleString('en-US', {
          maximumFractionDigits: 2
        })
    : value;

  return (
    <Field
      className={clsx(cls.main, className)}
      focused={focused}
      ref={ref}
      endAdornment={endAdornmentComp}
      {...rest}
    >
      {multiline ? (
        <textarea
          rows={rows}
          ref={inputRef as Ref<HTMLTextAreaElement> | null}
          disabled={disabled}
          {...inputElProps}
          {...textareaProps}
        ></textarea>
      ) : (
        <input
          ref={inputRef as Ref<HTMLInputElement> | null}
          maxLength={maxLength}
          {...inputElProps}
          {...inputProps}
          disabled={disabled}
          readOnly={readOnly}
          step={step}
          value={useCommaOrValue}
          type={showComma ? 'text' : type}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (
              (e.key === 'e' || e.key === 'E') &&
              !value &&
              type === 'number'
            ) {
              if (onChange) {
                onChange('0');
              }
            }
          }}
        />
      )}
    </Field>
  );
});

export default TextField;

/**
 * Assign classNames to component based on properties
 */
function styleTextField(props: TextFieldProps) {
  const mc = 'TextField';
  const cls = {
    main: [mc],
    input: ['Field__Input', `${mc}__Input`]
  };
  if (props.multiline) {
    cls.main.push(`${mc}--multiline`);
  }
  return cls;
}
